import React, { useMemo } from "react";
import { find, get, head, isNumber, isString } from "lodash";
import { map } from "lodash";
import { Controller, useForm } from "react-hook-form";
import { tr } from "@linkfluence/i18n";
import { Locale, getAllLocales } from "@linkfluence/models/locale";
import { Mode, getAllModes, isDarkMode } from "@linkfluence/models/mode";
import { Timezone } from "@linkfluence/models/timezone";
import { User } from "@linkfluence/models/user";
import { getTimezones } from "@tootools/date";
import Button from "@ui-kit/button";
import Form, { Fieldset, Footer, Label, Select } from "@ui-kit/form";

interface IProps {
  user: User;
  onUpdateUser: (data: Partial<User>) => void;
  disabledMode?: boolean;
  allowLocaleDebug?: boolean;
}

type Inputs = {
  theme: Mode;
  locale: Locale;
  timezone: Timezone;
};

const getSelectedLabel = (list: { value: string | number }[], value: any) =>
  find(list, {
    value: isString(value) || isNumber(value) ? value : get(value, "value"),
  }) ||
  head(list) || {
    value: "",
    id: "",
  };

const getThemeWithLabelObjects = (
  locale: Locale
): { value: Mode; label: string }[] =>
  map(getAllModes(), (theme) => ({
    value: theme,
    label: tr(
      locale,
      isDarkMode(theme) ? "PROFILE::THEMES::DARK" : "PROFILE::THEMES::LIGHT"
    ),
  }));

const TIMEZONES: { value: string; label: string }[] = map(
  getTimezones(),
  (timezone: Timezone) => ({
    value: timezone,
    label: timezone,
  })
);

const useCustoForm = (
  theme,
  locale,
  timezone,
  onUpdateUser,
  allowLocaleDebug
) => {
  const { handleSubmit, reset, control } = useForm<Inputs>({
    mode: "onChange",
    defaultValues: { theme, locale, timezone },
  });

  const LOCALES: {
    value: string;
    label: string;
  }[] = useMemo(
    () =>
      map(getAllLocales(allowLocaleDebug), ({ id, shorten }) => ({
        value: id,
        label: tr(locale, "LANGUAGES::" + shorten),
      })),
    [allowLocaleDebug, locale]
  );

  const handlers = useMemo(
    () => ({
      onSave: (data: Inputs) => {
        onUpdateUser(data);
      },
      onReset: (event) => {
        reset({ theme, locale, timezone });
        event.preventDefault();
        event.stopPropagation();
      },
    }),
    [locale, onUpdateUser, reset, theme, timezone]
  );

  return [control, handleSubmit, handlers, LOCALES] as const;
};

const UiKitProfileCusto = ({
  user: { theme, locale, timezone },
  onUpdateUser,
  disabledMode,
  allowLocaleDebug,
}: IProps) => {
  const [control, handleSubmit, { onSave, onReset }, LOCALES] = useCustoForm(
    theme,
    locale,
    timezone,
    onUpdateUser,
    allowLocaleDebug
  );

  const themes = getThemeWithLabelObjects(locale);

  return (
    <Form>
      <Fieldset>
        <Label htmlFor="theme">{tr(locale, "PROFILE::THEMES::TITLE")}</Label>
        <Controller
          name="theme"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Select
              variant="primary"
              value={getSelectedLabel(themes, value)}
              options={themes}
              onChange={(v) => onChange(v.value)}
              isDisabled={disabledMode}
              size="l"
            />
          )}
        />
      </Fieldset>
      <Fieldset>
        <Label htmlFor="locale">{tr(locale, "PROFILE::LANGUAGE::TITLE")}</Label>
        <Controller
          name="locale"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Select
              variant="primary"
              value={getSelectedLabel(LOCALES, value)}
              options={LOCALES}
              onChange={(v) => onChange(v.value)}
              size="l"
            />
          )}
        />
      </Fieldset>
      <Fieldset>
        <Label htmlFor="timezone">
          {tr(locale, "PROFILE::TIMEZONES::TITLE")}
        </Label>
        <Controller
          name="timezone"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Select
              variant="primary"
              name="timezone"
              value={getSelectedLabel(TIMEZONES, value)}
              options={TIMEZONES}
              isClearable={false}
              placeholder={tr(locale, "PROFILE::TIMEZONES::TITLE")}
              onChange={(v) => onChange(v.value)}
              size="l"
            />
          )}
        />
      </Fieldset>
      <Footer>
        <Button
          onClick={onReset}
          text={tr(locale, "_::CANCEL")}
          variant="tertiary"
        />
        <Button
          onClick={handleSubmit(onSave)}
          text={tr(locale, "_::SAVE")}
          variant="primary"
        />
      </Footer>
    </Form>
  );
};

export default UiKitProfileCusto;
